<template>
  <v-form ref="form" v-model="valid" lazy-validation>
    <div class="subtitle1 mb-4">
      {{ $t("modals.addAccessPermissions.assignUsers.title") }}
    </div>

    <div class="mb-6">
      <button-toggle-group
        v-model="localValue.action"
        :items="actionOptions"
        class="flex-grow-1 w-100 add-permission-modal-toggle"
        density="comfortable"
        bg-color="indigo-faint"
        data-testid="add-permission-modal-action-switch"
      />
      <div
        v-if="localValue.action === DataLossAction.ADD_UNPROTECTED_INTERNAL_USERS"
        class="body1 mt-8"
        v-html="$t(`modals.${DataLossAction.ADD_UNPROTECTED_INTERNAL_USERS}.description`)"
      />
    </div>

    <v-autocomplete
      v-if="localValue.action === DataLossAction.ADD_PERMISSION_FOR_SPECIFIC_GROUPS"
      v-model="localValue.groups"
      v-model:search="groupsSearchTerm"
      data-testid="add-permission-modal-groups-autocomplete"
      class="mb-8"
      :label="$t('modals.addNewGroup.label')"
      :placeholder="$t('modals.addNewGroup.placeholder')"
      :hint="$t('modals.addPermissionForSpecificGroups.hint')"
      item-title="name"
      return-object
      :no-filter="true"
      multiple
      auto-select-first="exact"
      clear-on-select
      persistent-hint
      :rules="[atLeastOneIsRequired()]"
      :hide-no-data="!groupsSearchTerm?.length"
      :loading="dataLoading"
      :menu-props="{ maxHeight: '250' }"
      :items="groups"
      variant="outlined"
      @update:search="getUserGroups($event)"
    >
      <template #message="{ message }">
        <div class="d-flex align-center">
          <v-icon icon="$infoXs" class="mr-1 flex-shrink-0" size="16" />
          <span>{{ message }}</span>
        </div>
      </template>
      <template v-slot:chip="{ props, item }">
        <v-chip
          v-bind="props"
          :closable="true"
          variant="flat"
          size="default"
          close-icon="$closeCircle"
          :text="item.title"
          color="indigo-faint"
        >
          <div class="d-flex align-center">
            <coro-icon class="service-icon" :icon-name="item.raw.service"></coro-icon>
            <span class="ml-1 mr-2">{{ item.title }}</span>
          </div>
        </v-chip>
      </template>
      <template #item="{ props, item }">
        <v-list-item v-bind="props" title="" :data-testid="`item-${camelCase(item.title)}`">
          <div class="d-flex align-center">
            <coro-icon class="service-icon" :icon-name="item.raw.service"></coro-icon>
            <span class="ml-1">{{ item.title }}</span>
          </div>
        </v-list-item>
      </template>
    </v-autocomplete>

    <v-combobox
      v-if="localValue.action === DataLossAction.ADD_PERMISSION_FOR_SPECIFIC_USERS"
      v-model="localValue.users"
      data-testid="add-permission-modal-users-domains-autocomplete"
      class="mt-3 mb-8"
      :loading="dataLoading"
      :items="users"
      :label="$t(`modals.${DataLossAction.ADD_PERMISSION_FOR_SPECIFIC_USERS}.label`)"
      :aria-label="$t(`modals.${DataLossAction.ADD_PERMISSION_FOR_SPECIFIC_USERS}.label`)"
      :placeholder="$t(`modals.${DataLossAction.ADD_PERMISSION_FOR_SPECIFIC_USERS}.placeholder`)"
      :rules="[emailOrDomainRules, atLeastOneIsRequired()]"
      maxlength="150"
      variant="outlined"
      multiple
      no-filter
      return-object
      item-value="userId"
      item-title="email"
    >
      <template v-slot:chip="{ props, item }">
        <v-chip
          v-bind="props"
          :closable="true"
          variant="flat"
          size="default"
          close-icon="$closeCircle"
          :color="getChipColor(item.value)"
          :text="item.title"
        >
          <div class="d-flex align-center">
            <span class="ml-1 mr-2">{{ item.title }}</span>
          </div>
        </v-chip>
      </template>
      <template #item="{ item }">
        <v-list-item :data-testid="`item-${camelCase(item.raw.email)}`">
          <span class="ml-1">{{ item.raw.email }}</span>
        </v-list-item>
      </template>
    </v-combobox>

    <v-autocomplete
      v-if="localValue.action === DataLossAction.ADD_PERMISSION_FOR_LABELS"
      v-model="localValue.labels"
      v-model:search="labelSearchTerm"
      data-testid="add-permission-modal-labels-autocomplete"
      class="mb-8"
      :items="labelsOptions"
      :label="$t('modals.addPermissionForLabels.label')"
      :placeholder="$t('modals.addPermissionForLabels.placeholder')"
      :rules="[atLeastOneIsRequired()]"
      variant="outlined"
      item-value="id"
      item-title="name"
      :no-data-text="$t('modals.addAccessPermissions.label.noData')"
      :loading="dataLoading"
      clear-on-select
      return-object
      multiple
      @update:search="getLabels($event)"
    >
      <template v-slot:chip="{ props, item }">
        <v-chip
          v-bind="props"
          size="default"
          :color="item.raw.color"
          density="compact"
          :text="item.raw.name"
          variant="flat"
          label
          rounded="3"
        >
          <span class="coro-label text-uppercase text-primary" v-text="item.raw.name" />
        </v-chip>
      </template>
      <template #item="{ item, props }">
        <v-list-item v-bind="props" title="">
          <div class="d-flex align-center label-popup-block">
            <div class="color-preview mr-2" :style="{ 'background-color': item.raw.color }" />
            <span>{{ item.raw.name }}</span>
          </div>
        </v-list-item>
      </template>
    </v-autocomplete>

    <div class="subtitle1 mb-4">
      {{ $t("userDataGovernanceSettings.dataLossModals.selectDataType") }}
    </div>

    <v-select
      v-model="localValue.selectedDataType"
      class="mb-8"
      :items="monitoredDataPolicies"
      :label="$t('userDataGovernanceSettings.dataLossModals.policy')"
      :placeholder="$t('userDataGovernanceSettings.dataLossModals.policy')"
      variant="outlined"
      hide-details
      :loading="selectedDataTypeLoading"
      data-testid="add-permission-modal-permission-select"
    >
      <template #selection="{ item }">
        <span class="body2">
          {{ $t(`userDataGovernanceSettings.dataLossModals.dataTypes.${item.title}`) }}
        </span>
      </template>
      <template #item="{ props, item }">
        <v-list-item
          v-bind="props"
          :title="$t(`userDataGovernanceSettings.dataLossModals.dataTypes.${item.title}`)"
        ></v-list-item>
      </template>
    </v-select>

    <div class="subtitle1">
      {{ $t("userDataGovernanceSettings.permissionsTab.permission") }}
    </div>
    <v-radio-group v-model="localValue.choice">
      <v-radio
        class="mt-4"
        v-for="choice in choices"
        :key="choice"
        :data-testid="`add-permission-modal-permission-select-item-${camelCase(choice)}`"
        :label="$t(`userDataGovernanceSettings.permissionsTab.actions.${camelCase(choice)}`)"
        :value="choice"
      ></v-radio>
    </v-radio-group>
  </v-form>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { camelCase, debounce, uniqBy } from "lodash";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import CoroIcon from "@/components/CoroIcon.vue";
import ButtonToggleGroup, { type ButtonToggleItem } from "@/components/ButtonToggleGroup.vue";
import { atLeastOneIsRequired } from "@/_helpers/validators";
import { useGroupsSettingsTabStore } from "@/_store/users-settings/user-groups-tab.module";
import { useUserDataGovernanceModule } from "@/_store/user-data-governance/user-data-governance.module";
import { useUserLabelsStore, type UserLabel } from "@/_store/users-settings/user-labels.module";
import Patterns from "@/constants/patterns";
import { DataLossAction, DataLossPermissionChoice } from "@/constants/user-data-governance";
import { TicketTrigger } from "@/constants/tickets";
import type { VuetifyFormRef } from "@/types";

interface AddPermissionModalForm {
  action: DataLossAction;
  choice: DataLossPermissionChoice;
  selectedDataType: TicketTrigger | null;
  users: Array<string | { email: string }>;
  groups: Array<{ name: string; service: string }>;
  labels: UserLabel[];
}

export default defineComponent({
  components: {
    ButtonToggleGroup,
    CoroIcon,
  },
  props: {
    value: {
      type: [Array, Object],
      required: true,
    },
    config: {
      type: Object,
      required: true,
    },
  },
  emits: ["update:localValue", "update:valid"],
  setup(props, { emit }) {
    const userDataGovernanceModule = useUserDataGovernanceModule();
    const { getMonitoredDataPolicies } = userDataGovernanceModule;
    const { monitoredDataPolicies } = storeToRefs(userDataGovernanceModule);
    const i18n = useI18n();
    const userGroupsStore = useGroupsSettingsTabStore();
    const userLabelsStore = useUserLabelsStore();
    const dataLoading = ref(false);
    const selectedDataTypeLoading = ref(false);
    const localValue = ref<AddPermissionModalForm>({
      users: [],
      groups: [],
      labels: [],
      action: props.config.action ?? DataLossAction.ADD_PERMISSION_FOR_ALL_USERS,
      choice: DataLossPermissionChoice.CAN_ACCESS,
      selectedDataType: null,
    });
    const users = ref([] as { email: string }[]);
    const groups = ref([] as { name: string; service: string }[]);
    const labelsOptions = ref<UserLabel[]>([]);
    const valid = ref(true);
    const form = ref<VuetifyFormRef>();
    const groupsSearchTerm = ref("");
    const labelSearchTerm = ref<string | undefined>("");

    watch(
      localValue,
      debounce((value) => {
        emit("update:localValue", value);
      }, 500),
      { deep: true }
    );

    onMounted(async () => {
      selectedDataTypeLoading.value = true;
      try {
        await getMonitoredDataPolicies();
        await getLabels();
        await getUserGroups();
        localValue.value.selectedDataType = monitoredDataPolicies.value[0] as TicketTrigger;
        emit("update:localValue", localValue.value);
      } catch (e) {
        console.log(e);
      } finally {
        selectedDataTypeLoading.value = false;
      }
    });

    const actionOptions = computed<ButtonToggleItem<DataLossAction>[]>(() => {
      const allUnprotectedInternalUsersPolicyLabel = i18n.t(
        "userDataGovernanceSettings.permissionsTab.addPermissionOptions.allUnprotectedInternalUsersPolicy"
      );

      return [
        {
          value: DataLossAction.ADD_PERMISSION_FOR_ALL_USERS,
          label: i18n.t("modals.addAccessPermissions.assignUsers.userType.all"),
        },
        {
          value: DataLossAction.ADD_PERMISSION_FOR_SPECIFIC_GROUPS,
          label: i18n.t("modals.addAccessPermissions.assignUsers.userType.group"),
        },
        {
          value: DataLossAction.ADD_PERMISSION_FOR_SPECIFIC_USERS,
          label: i18n.t("modals.addAccessPermissions.assignUsers.userType.user"),
        },
        {
          value: DataLossAction.ADD_UNPROTECTED_INTERNAL_USERS,
          label: allUnprotectedInternalUsersPolicyLabel,
          tooltip: allUnprotectedInternalUsersPolicyLabel,
        },
        {
          value: DataLossAction.ADD_PERMISSION_FOR_LABELS,
          label: i18n.t("modals.addAccessPermissions.assignUsers.userType.label"),
        },
      ];
    });

    const choices = computed(() => {
      if (localValue.value.action === DataLossAction.ADD_UNPROTECTED_INTERNAL_USERS) {
        return [DataLossPermissionChoice.CAN_ACCESS];
      }

      return [DataLossPermissionChoice.CAN_ACCESS, DataLossPermissionChoice.CAN_ACCESS_AND_EXPOSE];
    });

    watch(
      () => localValue.value.action,
      (action) => {
        if (action !== DataLossAction.ADD_PERMISSION_FOR_SPECIFIC_USERS) {
          localValue.value.users = [];
        }

        if (action !== DataLossAction.ADD_PERMISSION_FOR_SPECIFIC_GROUPS) {
          localValue.value.groups = [];
        }

        if (action !== DataLossAction.ADD_PERMISSION_FOR_LABELS) {
          localValue.value.labels = [];
        }

        if (action === DataLossAction.ADD_UNPROTECTED_INTERNAL_USERS) {
          localValue.value.choice = DataLossPermissionChoice.CAN_ACCESS;
        }
      },
      { immediate: true }
    );

    const validate = async () => {
      const validationResult = await form.value?.validate();
      return validationResult?.valid;
    };

    watch(valid, (value) => {
      if (value === null) {
        return;
      }

      emit("update:valid", value);
    });

    const getUserGroups = debounce(async (newVal: string = "") => {
      dataLoading.value = true;

      try {
        const response = await userGroupsStore.searchProtectedGroups(newVal);
        groups.value = [...localValue.value.groups, ...response.data.items];
      } finally {
        dataLoading.value = false;
      }
    }, 500);

    const getLabels = debounce(async (searchTerm: string = "") => {
      labelsOptions.value = [...localValue.value.labels];

      dataLoading.value = true;

      try {
        const response = await userLabelsStore.searchUserLabels(searchTerm);
        labelsOptions.value = uniqBy([...localValue.value.labels, ...response], "id");
      } finally {
        dataLoading.value = false;
      }
    }, 500);

    const getChipColor = (email: string) => {
      const isEmailOrDomainValid = Patterns.EMAIL.test(email) || Patterns.DOMAIN.test(email);
      return isEmailOrDomainValid ? "indigo-faint" : "error";
    };

    const emailOrDomainRules = (values: Array<string | { email: string }>) => {
      if (!values?.length) {
        return true;
      }

      for (const value of values) {
        const email = typeof value === "string" ? value : value.email;

        if (!Patterns.EMAIL.test(email) && !Patterns.DOMAIN.test(email)) {
          return i18n.t("validations.emailOrDomain");
        }
      }

      return true;
    };

    return {
      actionOptions,
      atLeastOneIsRequired,
      camelCase,
      choices,
      dataLoading,
      DataLossAction,
      emailOrDomainRules,
      form,
      getChipColor,
      groups,
      groupsSearchTerm,
      labelsOptions,
      labelSearchTerm,
      localValue,
      monitoredDataPolicies,
      getUserGroups,
      getLabels,
      selectedDataTypeLoading,
      users,
      valid,
      validate,
    };
  },
});
</script>

<style lang="scss" scoped>
.color-preview {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  border-radius: 2px !important;
}

.service-icon {
  height: 22px;
  width: 22px;
  padding-top: 2px;
}

:deep(.add-permission-modal-toggle) {
  .v-btn.toggle-button {
    min-width: 0;
  }

  .v-btn.toggle-button .v-btn__content {
    display: block;
    overflow: hidden;
  }

  .v-btn.toggle-button .v-btn__content > span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
